import { useRecoilValue } from 'recoil';
import type { ReplanningStageId } from '~/state/atoms/adaptiveReplanningAtoms';
import {
    replanningQaMeasurementState,
    replanningRunOutputsState,
    replanningVisibleRegisterTabState,
} from '~/state/atoms/adaptiveReplanningAtoms';
import { usePropagationRun } from './usePropagationRun';
import { useRegistrationQaRun } from './useRegistrationQaRun';
import { useSyntheticImagingRun } from './useSyntheticImagingRun';
import type { ReplanningStageStatus } from './useReplanningStageStatus';

export interface StagePrimaryAction {
    readonly labelId: string;
    readonly disabled: boolean;
    readonly reasonId?: string;
    readonly isRun: boolean;
    readonly isRunning: boolean;
    readonly press: () => void;
}

export function useStagePrimaryAction(
    currentStageId: ReplanningStageId,
    status: ReplanningStageStatus,
    onGoToStage: (stageId: ReplanningStageId) => void
): StagePrimaryAction {
    const registerTab = useRecoilValue(replanningVisibleRegisterTabState);
    const { propagatedUid } = useRecoilValue(replanningRunOutputsState);
    const propagation = usePropagationRun();
    const qa = useRegistrationQaRun();
    const qaMeasurement = useRecoilValue(replanningQaMeasurementState);
    const synthesis = useSyntheticImagingRun();

    const goOn = {
        labelId: 'replanning.continue',
        disabled: !status.canContinue,
        reasonId: status.continueReasonId,
        isRun: false,
        isRunning: false,
        press: () => status.nextStageId && onGoToStage(status.nextStageId),
    };

    if (currentStageId === 'synthetic-imaging' && synthesis.owesRun) {
        return {
            labelId: synthesis.isRunning
                ? 'replanning.prepare.generating'
                : 'replanning.prepare.generate',
            disabled: !synthesis.canRun,
            reasonId: synthesis.isRunning
                ? 'replanning.continue.busy'
                : synthesis.canRun
                  ? undefined
                  : 'replanning.prepare.incomplete',
            isRun: true,
            isRunning: synthesis.isRunning,
            press: synthesis.run,
        };
    }

    if (
        currentStageId === 'register' &&
        registerTab === 'process' &&
        !propagatedUid
    ) {
        return {
            labelId: propagation.isRunning
                ? 'replanning.propagate.working'
                : 'replanning.propagate.action',
            disabled: !propagation.canRun,
            reasonId: propagation.isRunning
                ? 'replanning.continue.busy'
                : propagation.canRun
                  ? undefined
                  : (propagation.blockedReasonId ??
                    'replanning.propagate.incomplete'),
            isRun: true,
            isRunning: propagation.isRunning,
            press: propagation.run,
        };
    }

    // The measurement starts by itself; the step waits for it rather than offering to start it.
    if (
        currentStageId === 'registration-qa' &&
        (qa.phase === 'segmenting' || qaMeasurement?.status === 'measuring')
    ) {
        return {
            ...goOn,
            disabled: true,
            reasonId: 'replanning.qa.busy',
            isRunning: true,
        };
    }

    return goOn;
}
